import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import ProgressButton from "../components/ProgressButton";
import bookingApi from "../service/bookingApi";
import { format } from "date-fns";
import { ClipLoader } from "react-spinners";
import { ToastContext } from "../contexts/ToastProvider";

const Wrapper = styled.div`
  gap: 10px;
  justify-content: center;
  padding: 10px;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  margin-top: 10vh;
  align-items: center;
`;

const TimeSlotWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  max-width: 75%;
`;

const TimeSlotButton = styled.button`
  padding: 10px 15px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 5px;
  background-color: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.primary : theme.colors.white};
  color: ${({ isSelected, theme }) =>
    isSelected ? theme.colors.white : theme.colors.primary};
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.primaryDark : theme.colors.primary};
    color: ${({ isSelected, theme }) =>
      isSelected ? theme.colors.white : theme.colors.white};
  }
`;

function Booking({ onNext, onBack }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const { toast } = useContext(ToastContext);

  const generateAllTimeSlots = () => {
    const slots = [];
    const now = new Date();
    let currentHour = 9;
    let currentMinute = 0;

    if (selectedDate === format(now, "yyyy-MM-dd")) {
      currentHour = now.getHours();
      currentMinute = now.getMinutes();

      if (currentMinute < 30) {
        currentMinute = 30;
      } else {
        currentMinute = 0;
        currentHour += 1;
      }
    }

    for (let hour = currentHour; hour <= 15; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === currentHour && minute < currentMinute) continue;

        if (
          (hour >= 9 && hour < 12) ||
          (hour === 12 && minute >= 30) ||
          (hour > 12 && hour < 15) ||
          (hour === 15 && minute < 30)
        ) {
          const time = `${String(hour).padStart(2, "0")}:${String(
            minute
          ).padStart(2, "0")}`;
          slots.push(time);
        }
      }
    }

    return slots;
  };

  const handleDateChange = (date) => {
    if (!date) return;
    const formattedDate = format(date, "yyyy-MM-dd");
    setSelectedDate(formattedDate);
  };

  useEffect(() => {
    if (selectedDate) {
      bookingApi
        .getBookingId(selectedDate, selectedDate)
        .then((response) => {
          setBookedSlots(response || []);
        })
        .catch((error) => {
          console.error("Error fetching booked slots:", error);
          setBookedSlots([]);
        });
    }
  }, [selectedDate]);

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleNext = () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Vælg venligst en dato og et tidspunkt.");
      return;
    }
    onNext({ date: selectedDate, time: selectedTime });
  };

  return (
    <Wrapper>
      <h1>Vælg en tid</h1>
      <p>Vælg en dato og et tidspunkt for din booking.</p>
      <Calendar onChange={handleDateChange} minDate={new Date()} />

      {selectedDate && bookedSlots.length > 0 && (
        <TimeSlotWrapper>
          {bookedSlots.flatMap((slot) =>
            slot.interval
              .flatMap(({ from, to }) => {
                const slots = [];
                let [fromHour, fromMinute] = from.split(":").map(Number);
                const [toHour, toMinute] = to.split(":").map(Number);
                const now = new Date();

                if (selectedDate === format(now, "yyyy-MM-dd")) {
                  const currentHour = now.getHours();
                  const currentMinute = now.getMinutes();
                  const nextAvailableMinute =
                    Math.ceil(currentMinute / 30) * 30;

                  if (
                    fromHour < currentHour ||
                    (fromHour === currentHour &&
                      fromMinute < nextAvailableMinute)
                  ) {
                    fromHour = currentHour;
                    fromMinute = nextAvailableMinute;
                  }
                }

                while (
                  fromHour < toHour ||
                  (fromHour === toHour && fromMinute < toMinute)
                ) {
                  while (fromMinute >= 60) {
                    fromMinute -= 60;
                    fromHour += 1;
                  }

                  if (
                    fromHour > toHour ||
                    (fromHour === toHour && fromMinute >= toMinute)
                  ) {
                    break;
                  }

                  const timeString = `${String(fromHour).padStart(
                    2,
                    "0"
                  )}:${String(fromMinute).padStart(2, "0")}`;
                  slots.push(timeString);

                  fromMinute += 30;
                }

                return slots;
              })
              .map((time, index) => (
                <TimeSlotButton
                  key={index}
                  isSelected={selectedTime === time}
                  onClick={() => handleTimeSelect(time)}
                >
                  {time}
                </TimeSlotButton>
              ))
          )}
        </TimeSlotWrapper>
      )}

      {selectedDate && bookedSlots.length === 0 && (
        <p style={{ color: "#018D66", marginTop: "20px" }}>
          Vi er ikke planlagt til at arbejde i dag.
        </p>
      )}

      <div style={{ marginTop: "20px" }}>
        <ProgressButton text="Tilbage" onClick={onBack} />
        <ProgressButton text="Videre" onClick={handleNext} />
      </div>
    </Wrapper>
  );
}

export default Booking;
